import React from "react";
import type { VehicleLabelDisplay } from "../../models/VehicleLabelDisplay";

export type VehicleLabelTemplateProps = {
  display: VehicleLabelDisplay;
  logoSrc?: string;
  qrSrc?: string;
  vrm?: string;
  makeModel?: string;
  stockNumber?: string;
  vin?: string;
  engineCode?: string;
  fuelType?: string;
  colour?: string;
  mileage?: string;
};

const safe = (value?: string) => (value ?? "").trim();

const lineOrNull = (enabled: boolean, label: string, value?: string) => {
  if (!enabled) return null;
  return `${label}: ${safe(value) || "-"}`;
};

export default function VehicleLabelTemplate({
  display,
  logoSrc,
  qrSrc,
  vrm,
  makeModel,
  stockNumber,
  vin,
  engineCode,
  fuelType,
  colour,
  mileage,
}: VehicleLabelTemplateProps) {
  const BASE_W = 10;
  const BASE_H = 5;
  const W = display.widthCm ?? BASE_W;
  const H = display.heightCm ?? BASE_H;
  const sx = W / BASE_W;
  const sy = H / BASE_H;
  const s = Math.min(sx, sy);
  const cw = (cm: number) => `${(cm * sx).toFixed(4)}cm`;
  const ch = (cm: number) => `${(cm * sy).toFixed(4)}cm`;
  const spx = (px: number) => `${Math.round(px * s)}px`;

  const leftStackLines = [
    lineOrNull(display.stockNumber, "Stock", stockNumber),
    lineOrNull(display.vin, "VIN", vin),
    lineOrNull(display.engineCode, "Engine", engineCode),
  ].filter((line): line is string => !!line);

  const rightStackLines = [
    lineOrNull(display.fuelType, "Fuel", fuelType),
    lineOrNull(display.colour, "Colour", colour),
    lineOrNull(display.mileage, "Mileage", mileage),
  ].filter((line): line is string => !!line);

  return (
    <html>
      <head>
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,maximum-scale=1,minimum-scale=1,user-scalable=no"
        />
      </head>
      <body style={{ margin: 0, padding: 0 }}>
        <div
          style={{
            width: `${W}cm`,
            height: `${H}cm`,
            display: "grid",
            gridTemplateColumns: `${cw(10)}`,
            gridTemplateRows: `${ch(1)} ${ch(1)} ${ch(3)}`,
            fontFamily: "Arial,Helvetica,sans-serif",
            boxSizing: "border-box",
            overflow: "hidden",
            border: "1px solid #111827",
          }}
        >
          <div
            style={{
              gridRow: 1 as any,
              display: "grid",
              gridTemplateColumns: `${cw(5)} ${cw(5)}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "start",
                fontWeight: 800,
                fontSize: spx(28),
                height: ch(1),
                paddingLeft: spx(2),
                letterSpacing: spx(1),
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {display.vrm ? safe(vrm) || "-" : null}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                height: ch(1),
                paddingRight: spx(6),
              }}
            >
              {display.logo && safe(logoSrc) ? (
                <img
                  src={logoSrc}
                  alt="Brand"
                  style={{
                    maxHeight: "100%",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : null}
            </div>
          </div>

          <div
            style={{
              gridRow: 2 as any,
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              fontWeight: 700,
              height: ch(1),
              lineHeight: 1,
              fontSize: spx(16),
              paddingLeft: spx(2),
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {display.makeModel ? safe(makeModel) : null}
          </div>

          <div
            style={{
              gridRow: 3 as any,
              display: "grid",
              gridTemplateColumns: `${cw(2)} ${cw(5)} ${cw(3)}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: spx(6),
                boxSizing: "border-box",
              }}
            >
              {display.qr && safe(qrSrc) ? (
                <img
                  src={qrSrc}
                  alt="QR"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : null}
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "end",
                gap: spx(2),
                textAlign: "center",
                fontSize: spx(12),
                lineHeight: 1.2,
                height: ch(3),
                paddingBottom: spx(6),
                boxSizing: "border-box",
              }}
            >
              {leftStackLines.map((line) => (
                <div key={line} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {line}
                </div>
              ))}
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "end",
                gap: spx(2),
                textAlign: "center",
                fontSize: spx(12),
                lineHeight: 1.2,
                height: ch(3),
                paddingBottom: spx(6),
                boxSizing: "border-box",
              }}
            >
              {rightStackLines.map((line) => (
                <div key={line} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {line}
                </div>
              ))}
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
